import type { Lang } from "@/i18n";

/**
 * Canonical page identifiers. The NL slug in `SLUGS[key].nl` is the
 * canonical `pathWithoutLang` value used everywhere in SEO/hreflang
 * bookkeeping. FR/EN slugs are the translated URLs used in the
 * corresponding `$lang.<slug>.tsx` routes and in localized links.
 */
export type PageKey =
  | "home"
  | "diensten"
  | "pakketten"
  | "proces"
  | "methode"
  | "expertise"
  | "technologie"
  | "over-ons"
  | "analyse"
  | "audit"
  | "second-opinion"
  | "voor-bureaus"
  | "contact"
  | "voorbeelden"
  | "woordenlijst"
  | "prijskaart"
  | "rapport"
  | "privacy"
  | "beveiliging"
  | "realisaties"
  | "blog-wat-kost";


export const SLUGS: Record<PageKey, Record<Lang, string>> = {
  home: { nl: "/", fr: "/", en: "/" },
  diensten: { nl: "/diensten", fr: "/services", en: "/services" },
  pakketten: { nl: "/pakketten", fr: "/forfaits", en: "/packages" },
  proces: { nl: "/proces", fr: "/processus", en: "/process" },
  methode: { nl: "/methode", fr: "/methode", en: "/method" },
  expertise: { nl: "/expertise", fr: "/expertise", en: "/expertise" },
  technologie: { nl: "/technologie", fr: "/technologie", en: "/technology" },
  "over-ons": { nl: "/over-ons", fr: "/a-propos", en: "/about" },
  analyse: { nl: "/analyse", fr: "/analyse", en: "/analysis" },
  audit: { nl: "/audit", fr: "/audit", en: "/audit" },
  "second-opinion": {
    nl: "/second-opinion",
    fr: "/avis-independant",
    en: "/independent-review",
  },
  "voor-bureaus": { nl: "/voor-bureaus", fr: "/pour-agences", en: "/for-agencies" },
  contact: { nl: "/contact", fr: "/contact", en: "/contact" },

  voorbeelden: { nl: "/voorbeelden", fr: "/exemples", en: "/examples" },
  woordenlijst: { nl: "/woordenlijst", fr: "/glossaire", en: "/glossary" },
  prijskaart: { nl: "/prijskaart", fr: "/tarifs", en: "/pricing-sheet" },
  rapport: { nl: "/rapport", fr: "/rapport-exemple", en: "/sample-report" },
  privacy: { nl: "/privacy", fr: "/confidentialite", en: "/privacy" },
  beveiliging: { nl: "/beveiliging", fr: "/securite", en: "/security" },
  realisaties: { nl: "/realisaties", fr: "/realisations", en: "/projects" },
  "blog-wat-kost": {
    nl: "/blog/wat-kost-een-website-laten-maken",
    fr: "/blog/prix-site-web",
    en: "/blog/website-cost",
  },
};

/** All canonical NL slugs — used as `pathWithoutLang` in SEO helpers. */
export const NL_SLUGS: readonly string[] = Object.values(SLUGS).map((s) => s.nl);

const NL_TO_KEY: Map<string, PageKey> = new Map(
  (Object.entries(SLUGS) as [PageKey, Record<Lang, string>][]).map(
    ([key, slugs]) => [slugs.nl, key],
  ),
);

export function pageKeyFromNlPath(path: string): PageKey | undefined {
  return NL_TO_KEY.get(path);
}

/**
 * Reverse lookup: vind de pagina-key voor een slug zoals die in een
 * bepaalde taal in de URL staat (bv. "/forfaits" in fr → "pakketten").
 * Valt terug op de NL-tabel zodat een NL-slug onder /fr ook herkend wordt.
 */
export function pageKeyFromLocalizedPath(path: string, lang: Lang): PageKey | undefined {
  const normalized = path === "" ? "/" : path.replace(/\/$/, "") || "/";
  for (const [key, slugs] of Object.entries(SLUGS) as [PageKey, Record<Lang, string>][]) {
    if (slugs[lang] === normalized) return key;
  }
  return NL_TO_KEY.get(normalized);
}

/** Given the canonical NL slug and a target lang, return the translated slug. */
export function localizedSlug(nlPath: string, lang: Lang): string {
  const key = NL_TO_KEY.get(nlPath);
  if (key) return SLUGS[key][lang];
  // Geneste paden (bv. "/woordenlijst/smart-routing"): vertaal het
  // basispad en behoud de rest van de URL ongewijzigd.
  const idx = nlPath.indexOf("/", 1);
  if (idx > 0) {
    const base = nlPath.slice(0, idx);
    const rest = nlPath.slice(idx);
    const baseKey = NL_TO_KEY.get(base);
    if (baseKey) return `${SLUGS[baseKey][lang]}${rest}`;
  }
  return nlPath;
}

/** Canoniek NL-pad van een woordenlijst-term (`/woordenlijst/<slug>`). */
export function glossaryTermNlPath(slug: string): string {
  return `${SLUGS.woordenlijst.nl}/${slug}`;
}

/** Publiek pad (met taalprefix) van een woordenlijst-term in een taal. */
export function glossaryTermPath(slug: string, lang: Lang): string {
  const path = `${SLUGS.woordenlijst[lang]}/${slug}`;
  return lang === "nl" ? path : `/${lang}${path}`;
}